// Pure mix state: what the user has set on the panel, and the per-source gains
// the engine should apply. Semantics follow Photon's slider helpers
// (cpopsa/src/state/sliders.hpp): solo is exclusive, muting clears solo, and
// bypass plays the untouched input.

import type { ModelDef } from './models';

export interface StemUi {
  gain: number;     // linear, 1 = 0 dB
  muted: boolean;
  soloed: boolean;
}

export type MixUi =
  | { kind: 'demix'; stems: StemUi[] }      // state order
  | { kind: 'restore'; strength: number };   // 0..1

export function defaultUi(model: ModelDef): MixUi {
  if (model.kind === 'demix') {
    return { kind: 'demix', stems: model.stems.map(() => ({ gain: 1, muted: false, soloed: false })) };
  }
  return { kind: 'restore', strength: model.defaultStrength };
}

function updateStem(ui: MixUi, index: number, f: (s: StemUi) => StemUi): MixUi {
  if (ui.kind !== 'demix' || index < 0 || index >= ui.stems.length) return ui;
  return { kind: 'demix', stems: ui.stems.map((s, i) => (i === index ? f(s) : s)) };
}

export function withGain(ui: MixUi, index: number, gain: number): MixUi {
  return updateStem(ui, index, (s) => ({ ...s, gain }));
}

export function withStrength(ui: MixUi, strength: number): MixUi {
  if (ui.kind !== 'restore') return ui;
  return { kind: 'restore', strength: Math.min(1, Math.max(0, strength)) };
}

// sliders.hpp withMuteToggle: a muted stem cannot stay soloed.
export function withMuteToggle(ui: MixUi, index: number): MixUi {
  return updateStem(ui, index, (s) => ({ ...s, muted: !s.muted, soloed: s.muted ? s.soloed : false }));
}

// ModelSettingsViews.cpp handleSoloClick: clicking the soloed stem clears
// solo everywhere (withAllUnmuted); otherwise this stem is soloed and every
// other stem is muted (withSolo).
export function withSoloToggle(ui: MixUi, index: number): MixUi {
  if (ui.kind !== 'demix' || index < 0 || index >= ui.stems.length) return ui;
  if (ui.stems[index].soloed) {
    return { kind: 'demix', stems: ui.stems.map((s) => ({ ...s, muted: false, soloed: false })) };
  }
  return {
    kind: 'demix',
    stems: ui.stems.map((s, i) => ({ ...s, muted: i !== index, soloed: i === index })),
  };
}

export function soloedIndex(ui: MixUi): number {
  return ui.kind === 'demix' ? ui.stems.findIndex((s) => s.soloed) : -1;
}

// Row dimming (alpha 0.4 in the app): muted, or another stem is soloed.
export function isDimmed(ui: MixUi, index: number): boolean {
  if (ui.kind !== 'demix') return false;
  const s = ui.stems[index];
  if (!s) return false;
  const solo = soloedIndex(ui);
  return s.muted || (solo >= 0 && solo !== index);
}

// Gain per audio source, in engine order (model.stems / [before, after]).
// Demix: muted or not-soloed stems are silent; bypass restores the plain sum
// of all stems, i.e. the original mix. Restore: lerp(before, after, strength),
// so strength 0 is an exact passthrough (featurelist.md "Plain-VE equivalence").
export function sourceGains(model: ModelDef, ui: MixUi, bypass: boolean): number[] {
  if (model.kind === 'demix') {
    if (ui.kind !== 'demix') throw new Error('mix state does not match a demix model');
    if (bypass) return model.stems.map(() => 1);
    const solo = soloedIndex(ui);
    return ui.stems.map((s, i) => {
      if (solo >= 0) return i === solo ? s.gain : 0;
      return s.muted ? 0 : s.gain;
    });
  }
  if (ui.kind !== 'restore') throw new Error('mix state does not match a restore model');
  if (bypass) return [1, 0];
  return [1 - ui.strength, ui.strength];
}
